/**
 * JobTableRow — Untitled UI style table row for the Jobs tab.
 * Columns: ☐ | Company | Job | Match % | Source | Role | Location | Skills | Posted | Actions
 */
import {
  Bookmark,
  BookmarkCheck,
  ChevronRight,
  Clock,
  ExternalLink,
  MapPin,
  Star,
  Zap,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CompanyLogoShared from '../../../../components/CompanyLogo';
import { supabase } from '../../../../lib/supabase';
import { useApplyClickLogger } from '../../hooks/useApplyClickLogger';
import { useJobContentTranslation } from '../../hooks/useJobContentTranslation';
import { cleanSkills, freshnessLabel, localizeJobType } from './helpers';
import PeerlistIcon from './PeerlistIcon';
import MatchScoreButton from '../MatchScoreButton';
import type { Job } from '../JobCard';

// ---------------------------------------------------------------------------
// CompanyLogoSmall — 32 px circular avatar
// ---------------------------------------------------------------------------

const CompanyLogoSmall: React.FC<{ url?: string; company: string }> = ({ url, company }) => (
  <CompanyLogoShared
    url={url}
    company={company}
    imgClassName="w-8 h-8 rounded-full object-contain shrink-0 p-0.5 border"
    imgStyle={{ background: 'var(--ds-bg-card)', borderColor: 'var(--ds-border)' }}
    fallbackClassName="ds-icon-square ds-icon-square-sm t-tag t-strong shrink-0 rounded-full"
    alt={`${company} logo`}
  />
);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface JobTableRowProps {
  job: Job;
  isSaved: boolean;
  isSelected?: boolean;
  isEven?: boolean;
  onSelect?: () => void;
  onToggleSave: (job: Job) => void;
  onApply: (job: Job) => void;
  onView: (job: Job) => void;
  onRowClick?: (job: Job) => void;
  onExplainMatch?: (job: Job) => void;
  isOwner?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const JobTableRow: React.FC<JobTableRowProps> = ({
  job,
  isSaved,
  isSelected = false,
  isEven = false,
  onSelect,
  onToggleSave,
  onApply,
  onView,
  onRowClick,
  onExplainMatch,
}) => {
  const { t, i18n } = useTranslation();
  const { title: displayTitle } = useJobContentTranslation(job, { translateDescription: false });
  const [isSignedIn, setIsSignedIn] = useState(false);
  const logClick = useApplyClickLogger();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setIsSignedIn(!!session));
  }, []);

  const handleApply = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const clickId = await logClick({ job, kind: 'optimize_and_apply' });
    sessionStorage.setItem('optimize.prefilledJob', JSON.stringify({
      jobUrl: job.url, jobTitle: job.title, companyName: job.company,
      source: job.source, externalJobId: job.external_job_id ?? null,
      clickId: clickId ?? undefined,
    }));
    onApply(job);
  }, [job, logClick, onApply]);

  const handleView = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const clickId = await logClick({ job, kind: 'view' });
    sessionStorage.setItem('pendingApplyConfirm', JSON.stringify({ clickId, job, ts: Date.now() }));
    onView(job);
    window.open(job.url, '_blank', 'noopener,noreferrer');
  }, [job, logClick, onView]);

  const handleSave = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSave(job);
  }, [job, onToggleSave]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const score = Math.round(job.matchScore ?? 0);

  const srcLabel =
    job.source_channel === 'linkedin' ? 'LinkedIn' :
    job.source_channel === 'naukri'   ? 'Naukri'   :
    job.source === 'peerlist'         ? 'Peerlist' :
    job.source_channel ?? null;

  const srcBadge = srcLabel
    ? <span className="ds-chip ds-chip-brand">
        {job.source === 'peerlist' && <PeerlistIcon size={8} />}
        {srcLabel}
      </span>
    : <span className="t-tag t-muted">—</span>;

  const allSkills  = cleanSkills(job.must_have_skills?.length ? job.must_have_skills : job.skills);
  const topSkills  = allSkills.slice(0, 3);
  const extraSkills = Math.max(0, allSkills.length - 3);
  const label      = freshnessLabel(job.posted_at ?? job.created, t, i18n.language);

  const rowBg = isSelected
    ? 'var(--ds-brand-soft)'
    : isEven ? 'var(--ds-bg-recessed)' : 'var(--ds-bg-card)';

  return (
    <tr
      className="group cursor-pointer transition-colors duration-100"
      style={{ background: rowBg, borderBottom: '1px solid var(--ds-divider)' }}
      onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--ds-bg-hover)'; }}
      onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = rowBg; }}
      onClick={() => onRowClick?.(job)}
    >
      {/* ── Checkbox ── */}
      {onSelect && (
        <td className="w-10 px-4 py-0" onClick={(e) => { e.stopPropagation(); onSelect(); }}>
          <input type="checkbox" className="w-3.5 h-3.5 rounded accent-[var(--ds-brand)] cursor-pointer"
            checked={isSelected} onChange={onSelect} onClick={(e) => e.stopPropagation()} />
        </td>
      )}

      {/* ── Company ── */}
      <td className="px-4 py-3" style={{ maxWidth: 180 }}>
        <div className="flex items-center gap-2.5">
          <CompanyLogoSmall url={job.company_logo_url} company={job.company} />
          <p className="t-meta truncate min-w-0">
            {job.company}
          </p>
        </div>
      </td>

      {/* ── Job title ── */}
      <td className="px-4 py-3" style={{ maxWidth: 220 }}>
        <p className="t-card-title truncate">
          {displayTitle}
        </p>
        {job.experience_level && (
          <p className="t-tag t-muted mt-0.5 truncate">
            {job.experience_level}
          </p>
        )}
      </td>

      {/* ── Match % — badge with colored dot ── */}
      <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        {score >= 25 ? (
          <MatchScoreButton
            score={score}
            variant="chip"
            onClick={onExplainMatch ? () => onExplainMatch(job) : undefined}
          />
        ) : (
          <span className="t-tag t-muted">—</span>
        )}
      </td>

      {/* ── Source ── */}
      <td className="px-4 py-3 whitespace-nowrap">{srcBadge}</td>

      {/* ── Role / Type ── */}
      <td className="px-4 py-3 whitespace-nowrap">
        {job.remote ? (
          <span className="ds-chip ds-chip-ok">
            <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
            {t('dashboard.jobs.jobCard.remote', 'Remote')}
          </span>
        ) : job.job_type ? (
          <span className="ds-chip ds-chip-info">
            {localizeJobType(job.job_type, t)}
          </span>
        ) : (
          <span className="t-tag t-muted">—</span>
        )}
      </td>

      {/* ── Location ── */}
      <td className="px-4 py-3 whitespace-nowrap">
        {job.location ? (
          <span className="t-meta flex items-center gap-1">
            <MapPin className="w-3 h-3 shrink-0 t-danger" />
            {job.location}
          </span>
        ) : (
          <span className="t-meta">—</span>
        )}
      </td>

      {/* ── Skills — cycling colored badges ── */}
      <td className="px-4 py-3">
        {topSkills.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {topSkills.map((s) => (
              <span key={s} className="ds-chip ds-chip-brand">
                {s}
              </span>
            ))}
            {extraSkills > 0 && (
              <span className="ds-chip t-num">
                +{extraSkills}
              </span>
            )}
          </div>
        ) : <span className="t-tag t-muted">—</span>}
      </td>

      {/* ── Posted ── */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="t-tag t-muted flex items-center gap-1">
          <Clock className="w-3 h-3 shrink-0" />
          {label || '—'}
        </span>
      </td>

      {/* ── Actions — hover-revealed on mouse, always visible on touch ── */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()} role="presentation">
        <div className="ds-row-actions flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Save / star */}
          <button type="button" title={isSaved ? t('dashboard.jobs.jobCard.saved', 'Saved') : t('dashboard.jobs.jobCard.saveJob', 'Save job')}
            onClick={handleSave} disabled={!isSignedIn}
            className="ds-btn ds-btn-ghost ds-btn-sm"
            style={{ color: 'var(--ds-warn)' }}>
            <Star className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
          </button>

          {/* Bookmark */}
          <button type="button" title={t('dashboard.jobs.bookmarkTitle', 'Bookmark')}
            onClick={handleSave} disabled={!isSignedIn}
            className="ds-btn ds-btn-ghost ds-btn-sm"
            style={{ color: isSaved ? 'var(--ds-brand)' : 'var(--ds-text-muted)' }}>
            {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
          </button>

          {/* Optimize & Apply */}
          <button type="button" title={t('dashboard.jobs.optimizeAndApply', 'Optimize & Apply')}
            onClick={handleApply}
            className="ds-btn ds-btn-primary ds-btn-sm">
            <Zap className="w-4 h-4" />
          </button>

          {/* View → */}
          <button type="button" title={t('dashboard.jobs.viewJobTitle', 'View job')}
            onClick={handleView}
            className="ds-btn ds-btn-secondary ds-btn-sm">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default React.memo(JobTableRow);
